import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Dictionary, CodeSet, Code, CodeLanguage } from '../model/dictionary';
import { Observable, forkJoin, Subject } from 'rxjs';
import { Cacheable, CacheBuster } from 'ts-cacheable';

const codeSetCacheBuster = new Subject<void>();
const codesCacheBuster = new Subject<void>();
const codeLanguageCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class DictionaryService
{
  constructor(private http: HttpClient) { }

  /**
   * Get all codes of a CodeSet by ID.
   *
   * @param codeSetId ID of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedCodesByCodeSetId(codeSetId: number): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codesetid/${codeSetId}`);
  }

  /**
   * Get all cached gender codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedGenders(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Gender`);
  }

  /**
   * Get all cached address types codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedAddressTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/AddressTypes`);
  }

  /**
   * Get all cached contact types codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedContactTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ContactType`);
  }

  /**
   * Get all cached flag priorities codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedFlagPriorities(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/FlagPriority`);
  }

  /**
   * Get all cached flag categories codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedFlagCategories(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/FlagCategory`);
  }

  /**
   * Get all cached frequency codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedFrequencies(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Frequencies`);
  }

  /**
   * Get all cached flags codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedFlags(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/DiagnosisCode`);
  }

  /**
   * Get all cached state or country codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedStatesOrProvinces(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/StateOrProvince`);
  }

  /**
   * Get all cached patient search sources.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedPatientSearchSources(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/PatientSearchSources`);
  }

  /**
   * Get all cached country codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedCountries(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Country`);
  }

  /**
   * Get all cached community service types.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedCommunityServiceTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/CommunityServiceType`);
  }

  /**
   * Get all cached community service correction types.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedCommunityServiceCorrectionTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/CommunityServiceCorrection`);
  }

  /**
   * Get all cached organization types.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedOrganizationTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/OrganizationType`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedSpecialties(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/HealthcareProviderTaxonomy`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedFamilyMembers(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/FamilyMembers`);
  }

  /**
   * Get all codes of used by Field Configurations.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedCodesUsedByFieldConfigurations(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/field/configurations`);
  }

  /**
   * Get all cached CodeSets.
   */
  @Cacheable(
    {
      cacheBusterObserver: codeSetCacheBuster
    })
  public getCachedCodeSets(): Observable<CodeSet[]>
  {
    return this.http.get<CodeSet[]>(`/dictionaries/codesets`);
  }

  /**
   * Get all minimal cached CodeSets.
   */
  @Cacheable(
    {
      cacheBusterObserver: codeSetCacheBuster
    })
  public getCachedMinimalCodeSets(): Observable<CodeSet[]>
  {
    return this.http.get<CodeSet[]>(`/dictionaries/codesets/minimal`);
  }

  /**
   * Creates a new Code.
   *
   * @param code The new code to create.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codesCacheBuster
    })
  public createCode(code: Code): Observable<Code>
  {
    return this.http.post<Code>(`/dictionaries`, code);
  }

  /**
   * Updates a Code.
   *
   * @param codeSet The Code to update.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codesCacheBuster
    })
  public updateCode(code: Code): Observable<Code>
  {
    return this.http.put<Code>(`/dictionaries/${code.id}`, code);
  }

  /**
   * Creates a set of codes.
   *
   * @param codes The new codes created.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codesCacheBuster
    })
  public createCodes(codes: Code[]): Observable<Code[]>
  {
    return this.http.post<Code[]>(`/dictionaries/batch`, codes);
  }

  /**
   * Deletes a Code.
   *
   * @param codeId Code ID.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codesCacheBuster
    })
  public deleteCode(codeId: number): Observable<Code>
  {
    return this.http.delete<Code>(`/dictionaries/${codeId}`);
  }

  /**
   * Creates a new CodeSet.
   *
   * @param codeSet The new CodeSet to create.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codeSetCacheBuster
    })
  public createCodeSet(codeSet: CodeSet): Observable<CodeSet>
  {
    return this.http.post<CodeSet>(`/dictionaries/codesets`, codeSet);
  }

  /**
   * Updates a CodeSet.
   *
   * @param codeSet The CodeSet to update.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codeSetCacheBuster
    })
  public updateCodeSet(codeSet: CodeSet): Observable<CodeSet>
  {
    return this.http.put<CodeSet>(`/dictionaries/codesets/${codeSet.id}`, codeSet);
  }

  /**
   * Deletes a Code Set.
   *
   * @param codeSetId CodeSet ID.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codeSetCacheBuster
    })
  public deleteCodeSet(codeSetId: number): Observable<CodeSet>
  {
    return this.http.delete<CodeSet>(`/dictionaries/codesets/${codeSetId}`);
  }

  /**
   * Creates a set of codes.
   *
   * @param codes The new codes created.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codeLanguageCacheBuster
    })
  public saveCodeLanguage(data: CodeLanguage, isNew: boolean, id?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/dictionaries/languages', data);
    }
    else
    {
      return this.http.put(`/dictionaries/languages/${id}`, data);
    }
  }

  @Cacheable(
    {
      cacheBusterObserver: codeLanguageCacheBuster
    })
  public getCachedLanguages(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Language`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codeLanguageCacheBuster
    })
  public getCachedEnhancedServices(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/EnhancedServices`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedNamePronouns(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Pronoun`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedEnglishProficiencies(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/EnglishProficiency`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedInterpreterNeeds(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/InterpreterNeed`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedRaces(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/RaceDetails`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedSexualOrientations(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/SexualOrientation`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedTribalAffiliations(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/TribalAffiliation`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedTribalEnrollments(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/TribalEnrollment`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedLicenses(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Licenses`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedProviderTaxonomies(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ProviderTaxonomies`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedAreaServedCities(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/AreaServedCities`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedAreaServedCounties(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/AreaServedCounties`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedAreaServedZipCodes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/AreaServedZipCodes`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedResourceStatuses(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ResourceStatus`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedIncomeRequirements(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/IncomeRequirements`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedPaymentMethods(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/PaymentMethods`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedTargetedDemographics(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/TargetedDemographics`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedTargetedPopulations(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/TargetedPopulations`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedConsentableDataTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ConsentableDataType`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedTotalChildren(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/TotalChildren`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedTotalAdults(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/TotalAdults`);
  }

  /**
   * Get all cached consent type codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedConsentTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ConsentTypes`);
  }

  /**
   * Get all cached consent signed by codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedConsentSignedBy(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ConsentSignedBy`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedExtractFunctions(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ExtractFunctions`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedMetricViewNames(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/MetricViewNames`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedExtractViewNames(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ExtractViewNames`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedSensitivities(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/RestrictedDataReasons`);
  }

  /**
   * Deletes a Code Language.
   *
   * @param id Code Language ID.
   */
  @CacheBuster(
    {
      cacheBusterNotifier: codeLanguageCacheBuster
    })
  public deleteCodeLanguage(id: number): Observable<CodeLanguage>
  {
    return this.http.delete<CodeLanguage>(`/dictionaries/languages/${id}`);
  }

  /**
   * Get all cached housing status codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedHousingStatuses(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/HousingStatus`);
  }

  /**
   * Get all cached veteran status codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedVeteranStatuses(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/VeteranStatus`);
  }

  /**
   * Get all identifier types.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedIdentifiers(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Identifiers`);
  }

  /**
   * Get all identifier sources.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedIdentifierSources(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/IdentifierSource`);
  }

  /**
   * Get all race ethnicities.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedRaceEthnicities(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/RaceEthnicity`);
  }

  /**
   * Get all education levels.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedEducationLevels(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/EducationLevel`);
  }

  /**
   * Get all employment statuses.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedEmploymentStatuses(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/EmploymentStatus`);
  }

  /**
   * Get all marital statuses.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedMaritalStatuses(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/MaritalStatus`);
  }

  /**
   * Get all religious affiliations.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedReligiousAffiliations(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ReligiousAffiliation`);
  }

  /**
   * Get all contact entities.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedContactEntities(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ContactEntity`);
  }

  /**
   * Get all contact intensities.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedContactIntensities(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ContactIntensity`);
  }

  /**
   * Get all contact activities.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedContactActivities(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ContactActivity`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedTypeOfGoal(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/TypeOfGoal`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedGoalProgress(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/GoalProgress`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedGoal(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Goal`);
  }

  /**
   * Get all consent reponses.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedConsentResponses(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ConsentResponse`);
  }

  /**
   * Get all name types.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedNameTypes(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/NameType`);
  }

  /**
   * Get all cached fee schedules.
   *
   * @param codeSetName Name of the CodeSet.
   */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedFeeScheduless(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/FeeSchedule`);
  }

  /**
 * Get all cached fee categories.
 *
 * @param codeSetName Name of the CodeSet.
 */
  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedFeeCategories(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/FeeCategory`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedOrganizationIdentifierTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/OrganizationIdentifierType`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedOrganizationIdentifierAssigningAuthority(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/OrganizationIdentifierAssigningAuthority`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedOrgPaymentStatus(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/OrganizationPaymentStatus`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedCommunityServiceIdentifierAssigningAuthority(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/CommunityServiceIdentifierAssigningAuthority`);
  }

  @Cacheable(
    {
      cacheBusterObserver: codesCacheBuster
    })
  public getCachedCommunityServiceIdentifierTypes(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/CommunityServiceIdentifierType`);
  } 

  /**
   * Get all resource statuses.
   */
  public getResourceStatuses(): Observable<any[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/ResourceStatus`);
  }

  /**
   * Get all CodeSets.
   */
  public getCodeSets(): Observable<CodeSet[]>
  {
    return this.http.get<CodeSet[]>(`/dictionaries/codesets`);
  }

  /**
   * Get all codes of a CodeSet by name.
   *
   * @param codeSetName Name of the CodeSet.
   */
  public getCodesByCodeSetName(codeSetName: string): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/${codeSetName}`);
  }

  /**
   * Get all codes of a CodeSet by ID.
   *
   * @param codeSetId ID of the CodeSet.
   */
  public getCodesByCodeSetId(codeSetId: number): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codesetid/${codeSetId}`);
  }

  /**
   * Check if CodeSet Name exists to avoid name duplicates
   *
   * @param name Code Set Name
   */
  public existsCodeSetName(name: string): Observable<any>
  {
    return this.http.get(`/dictionaries/codesets/exists?name=${name}`);
  }

  /**
   * Check if Code and Code System exists to avoid code duplicates
   *
   * @param name Code Set Name
   */
  public existsCode(codeSetId: number, code: string, codeSystem: string): Observable<any>
  {
    return this.http.get(`/dictionaries/exists?codeSetID=${codeSetId}&code=${code}&codeSystem=${codeSystem}`);
  }

  /**
   * Get all code languages of a Dictionary by ID.
   *
   * @param dictionaryId ID of the Dictionary.
   */
  public getCodeLanguagesByDictionaryId(dictionaryId: number): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/dictionary/${dictionaryId}/languages`);
  }

  /**
   * Get all flag categories codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  public getFlagCategories(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/FlagCategory`);
  }

  /**
   * Get all frequencies codes.
   *
   * @param codeSetName Name of the CodeSet.
   */
  public getFrequencies(): Observable<Dictionary[]>
  {
    return this.http.get<Dictionary[]>(`/dictionaries/codeset/Frequencies`);
  }

  public getFlagLookups(): Observable<any[]>
  {
    const priorities = this.getCachedFlagPriorities();
    const categories = this.getCachedFlagCategories();
    const flags = this.getCachedFlags();
    return forkJoin([priorities, categories, flags]);
  }

  public getExtractLookups(): Observable<any[]>
  {
    const metrics = this.getCachedMetricViewNames();
    const reports = this.getCachedExtractViewNames();
    return forkJoin([metrics, reports]);
  }
}
